// angular imports
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

// devextreme imports
import { DxPopupComponent } from 'devextreme-angular';
import { Column } from 'devextreme/ui/data_grid';

// cc-common imports
import { IconConstants, Utils } from 'cc-common';

// constant imports
import { WorkflowConstants } from '../../../shared-workflow-module/_constants/worflow-constants';

// service imports
import { CommunityService } from '../../../_services/data-services/community.service';
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { WorkflowPaymentRequestService } from '../../_services/workflow-payment-request.service';

// model imports
import { CommunitySelectorParameterDto } from '../../../_models/community-selector-parameter-dto';
import { CommunitySelectorResultDto } from '../../../_models/community-selector-result-dto';
import { WFParametersDto } from '../../../shared-workflow-module/_models/wf-parameter-dto';
import { WFEVendorResultDto } from '../../../shared-workflow-module/_models/wfe-vendor-result-dto';
import { WFEOwnerResultDto } from '../../../shared-workflow-module/_models/wfe-owner-result-dto';

@Component({
    selector: 'app-workflow-payment-request-payee-selector',
    templateUrl: './workflow-payment-request-payee-selector.component.html',
    styleUrls: ['./workflow-payment-request-payee-selector.component.scss']
})
export class WorkflowPaymentRequestPayeeSelectorComponent implements OnInit {
    @ViewChild('payeeSelectorPopup', { static: false }) payeeSelectorPopup: DxPopupComponent;

    @Input() communityId: number;

    @Output() onClose = new EventEmitter<boolean>();
    @Output() onPayeeSelection = new EventEmitter<any>();

    visible = false;

    payeeTabs: Array<object> = [];
    selectedTabPanel: any = null;

    communityList: Array<CommunitySelectorResultDto> = [];
    communityColumns: Array<Column> = [];

    vendorList: Array<WFEVendorResultDto> = [];
    vendorColumns: any[];

    ownerList: Array<WFEOwnerResultDto> = [];
    ownerColumns: any[];

    iconClose = IconConstants.CLOSE;
    isPayeeSelected = false;
    loadingVisible = true;

    constructor(
        private readonly communityService: CommunityService,
        private readonly workflowPaymentRequestService: WorkflowPaymentRequestService,
        private readonly errorService: ServerErrorService
    ) {}

    ngOnInit() {
        this.show();
    }

    show(): void {
        this.visible = true;
    }

    onPopUpShown(): void {
        this.payeeSelectorPopup.instance.repaint();
        this.configurePayeeTabs();
    }

    onPopUpClose(): void {
        this.closeSelector();
    }

    onCommunitySelection(record: CommunitySelectorResultDto): void {
        this.onPayeeSelection.emit({
            payeeCode: record.intacctCompanyId,
            payeeName: record.communityName,
            payeeType: WorkflowConstants.PAYEE_TYPE_COMMUNITY,
            payee: record
        });
        this.isPayeeSelected = true;
        this.closeSelector();
    }

    onOwnerSelection(record: WFEOwnerResultDto): void {
        this.onPayeeSelection.emit({
            payeeCode: record.customerId,
            payeeName: record.ownerName,
            payeeType: WorkflowConstants.PAYEE_TYPE_OWNER,
            payee: record
        });
        this.isPayeeSelected = true;
        this.closeSelector();
    }

    onVendorSelection(record: WFEVendorResultDto): void {
        this.onPayeeSelection.emit({
            payeeCode: record.vendorCode,
            payeeName: record.vendorName,
            payeeType: WorkflowConstants.PAYEE_TYPE_VENDOR,
            payee: record
        });
        this.isPayeeSelected = true;
        this.closeSelector();
    }

    onSelectionChanged(): void {
        this.onPayeeOptionChange();
    }

    onCloseClick(): void {
        this.closeSelector();
    }

    private onPayeeOptionChange(): void {
        if (Utils.notNullAndDefined(this.selectedTabPanel)) {
            if (Utils.equalsIgnoreCase(this.selectedTabPanel.name, WorkflowConstants.PAYEE_TYPE_VENDOR)) {
                this.getVendors();
            } else if (Utils.equalsIgnoreCase(this.selectedTabPanel.name, WorkflowConstants.PAYEE_TYPE_OWNER)) {
                this.getOwners();
            } else if (Utils.equalsIgnoreCase(this.selectedTabPanel.name, WorkflowConstants.PAYEE_TYPE_COMMUNITY)) {
                this.getCommunities();
            }
        }
    }

    private getVendors(): void {
        const baseParams: WFParametersDto = new WFParametersDto();
        baseParams.communityId = this.communityId;

        this.loadingVisible = true;

        this.workflowPaymentRequestService.getVendors(baseParams).subscribe(
            (result: WFEVendorResultDto[]) => {
                this.vendorList = result;
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private getOwners(): void {
        const baseParams: WFParametersDto = new WFParametersDto();
        baseParams.communityId = this.communityId;

        this.loadingVisible = true;

        this.workflowPaymentRequestService.getOwners(baseParams).subscribe(
            (result: WFEOwnerResultDto[]) => {
                this.ownerList = result;
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private getCommunities(clearCache = false): void {
        const baseParam: CommunitySelectorParameterDto = new CommunitySelectorParameterDto();
        baseParam.includeInactive = false;
        this.loadingVisible = true;
        this.communityService.getCommunities(clearCache).subscribe(
            (result: CommunitySelectorResultDto[]) => {
                this.communityList = result;
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private configurePayeeTabs(): void {
        this.payeeTabs = [
            {
                name: WorkflowConstants.PAYEE_TYPE_VENDOR,
                text: 'Vendor'
            },
            {
                name: WorkflowConstants.PAYEE_TYPE_OWNER,
                text: 'Owner'
            },
            {
                name: WorkflowConstants.PAYEE_TYPE_COMMUNITY,
                text: 'Communities'
            }
        ];

        this.selectedTabPanel = this.payeeTabs[0];

        this.getPayeeColumns();
    }

    private getPayeeColumns(): void {
        this.communityColumns = [
            {
                dataType: 'string',
                caption: 'Action',
                alignment: 'center',
                cellTemplate: 'selectButtonTemplate'
            },
            {
                dataField: 'locationName',
                dataType: 'string',
                caption: 'Location'
            },
            {
                dataField: 'communityName',
                dataType: 'string',
                caption: 'Community Name'
            }
        ];

        this.ownerColumns = [
            {
                dataType: 'string',
                caption: 'Action',
                alignment: 'center',
                cellTemplate: 'selectButtonTemplate'
            },
            {
                dataField: 'ownerName',
                dataType: 'string',
                caption: 'Owner Name'
            },
            {
                dataField: 'propertyAddress',
                dataType: 'string',
                caption: 'Property Address'
            },
            {
                dataField: 'customerId',
                dataType: 'string',
                caption: 'Account Number'
            },
            {
                dataField: 'isCurrentOwner',
                dataType: 'boolean',
                caption: 'Active',
                cellTemplate: 'activeIconTemplate'
            }
        ];

        this.vendorColumns = [
            {
                dataType: 'string',
                caption: 'Action',
                alignment: 'center',
                cellTemplate: 'selectButtonTemplate'
            },
            {
                dataField: 'vendorName',
                dataType: 'string',
                caption: 'Vendor Name'
            },
            {
                dataField: 'vendorTypeCode',
                dataType: 'string',
                caption: 'Type'
            },
            {
                dataField: 'remitAddress',
                dataType: 'string',
                caption: 'Address'
            },
            {
                dataField: 'isActive',
                dataType: 'boolean',
                caption: 'Active',
                cellTemplate: 'activeIconTemplate'
            }
        ];
    }

    private closeSelector(): void {
        this.visible = false;
        this.onClose.emit(this.isPayeeSelected);
    }
}
